import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { useAuth } from "../hooks/useAuth";
import { pluginApi, stripeApi, type Plugin, type PluginFile } from "../lib/api";
import {
  PlusIcon,
  DownloadIcon,
  StarIcon,
  TrendingUpIcon,
  GridIcon,
  ListIcon,
  TrashIcon,
  EyeIcon,
  UploadIcon,
  CheckIcon,
  XIcon,
  FileIcon,
  LinkIcon,
  GitHubIcon,
  FolderIcon,
  ChevronDownIcon,
  SparklesIcon,
  TerminalIcon,
  BotIcon,
  ZapIcon,
} from "../components/ui/Icons";

type ViewMode = "grid" | "list";

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [userPlugins, setUserPlugins] = useState<Plugin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletePluginId, setDeletePluginId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedPluginId, setCopiedPluginId] = useState<string | null>(null);
  const [initialUploadMode, setInitialUploadMode] = useState<
    "scratch" | "existing" | "github" | null
  >(null);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [statusPopoverPluginId, setStatusPopoverPluginId] = useState<string | null>(null);
  const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);
  const [pricingPluginId, setPricingPluginId] = useState<string | null>(null);
  const [pricingModel, setPricingModel] = useState<string>("FREE");
  const [priceInput, setPriceInput] = useState("");
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const uploadDropdownRef = useRef<HTMLDivElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const statusPopoverRef = useRef<HTMLDivElement>(null);

  const copyPluginUrl = (pluginSlug: string, pluginId: string) => {
    const url = `${window.location.origin}/plugin/${pluginSlug}`;
    navigator.clipboard.writeText(url);
    setCopiedPluginId(pluginId);
    setTimeout(() => setCopiedPluginId(null), 2000);
  };

  const loadPlugins = async () => {
    try {
      const plugins = await pluginApi.getMyPlugins();
      setUserPlugins(plugins);
    } catch (err) {
      console.error("Failed to load plugins:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPlugins();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (uploadDropdownRef.current && !uploadDropdownRef.current.contains(event.target as Node)) {
        setShowUploadDropdown(false);
      }
      if (statusPopoverRef.current && !statusPopoverRef.current.contains(event.target as Node)) {
        setStatusPopoverPluginId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmitForReview = async (pluginId: string) => {
    setIsSubmittingForReview(true);
    try {
      await pluginApi.submitForReview(pluginId);
      // Refresh the plugin list
      await loadPlugins();
      setStatusPopoverPluginId(null);
    } catch (err) {
      console.error("Failed to submit for review:", err);
    } finally {
      setIsSubmittingForReview(false);
    }
  };

  const handleDeletePlugin = async () => {
    if (!deletePluginId) return;

    setIsDeleting(true);
    try {
      await pluginApi.deletePlugin(deletePluginId);
      setUserPlugins((prev) => prev.filter((p) => p.id !== deletePluginId));
      setDeletePluginId(null);
    } catch (err) {
      console.error("Failed to delete plugin:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const pluginToDelete = userPlugins.find((p) => p.id === deletePluginId);
  const canDeletePlugin = (_status: string) => true;

  const openPricingModal = (plugin: Plugin) => {
    setPricingPluginId(plugin.id);
    setPricingModel(plugin.pricingModel || "FREE");
    setPriceInput(plugin.price ? (plugin.price / 100).toFixed(2) : "");
  };

  const handleSavePrice = async () => {
    if (!pricingPluginId) return;
    setIsSavingPrice(true);
    try {
      const priceInCents =
        pricingModel !== "FREE" && priceInput ? Math.round(parseFloat(priceInput) * 100) : null;
      await stripeApi.setPluginPricing(pricingPluginId, pricingModel, priceInCents);
      setPricingPluginId(null);
      await loadPlugins();
    } catch (err) {
      console.error("Failed to save pricing:", err);
    } finally {
      setIsSavingPrice(false);
    }
  };

  // Calculate stats from real data
  const stats = [
    {
      label: "Total Downloads",
      value: userPlugins.reduce((sum, p) => sum + (p._count?.downloads || 0), 0).toLocaleString(),
      change: "",
      icon: DownloadIcon,
    },
    {
      label: "Total Plugins",
      value: userPlugins.length.toString(),
      change: "",
      icon: GridIcon,
    },
    {
      label: "Published",
      value: userPlugins.filter((p) => p.status === "APPROVED" || p.published).length.toString(),
      change: "",
      icon: CheckIcon,
    },
    {
      label: "Pending",
      value: userPlugins.filter((p) => p.status === "PENDING_REVIEW").length.toString(),
      change: "",
      icon: EyeIcon,
    },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
      case "APPROVED":
        return "bg-success/20 text-success ring-success/30";
      case "DRAFT":
        return "bg-warning/20 text-warning ring-warning/30";
      case "PENDING_REVIEW":
        return "bg-blue-500/20 text-blue-400 ring-blue-500/30";
      case "REJECTED":
        return "bg-danger/20 text-danger ring-danger/30";
      default:
        return "bg-muted/20 text-muted ring-muted/30";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "Published";
      case "APPROVED":
        return "Approved";
      case "DRAFT":
        return "Draft";
      case "PENDING_REVIEW":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          {/* Welcome Section */}
          <div className="mb-12">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
              Welcome back, <span className="text-amber">{user?.name || "Developer"}</span>
            </h1>
            <p className="mt-2 text-muted">Manage your plugins and track their performance</p>
          </div>

          {/* Stats Cards */}
          <div className="mb-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="card-glow p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-elevated/50">
                    <stat.icon size={20} className="text-amber" />
                  </div>
                  {stat.change !== "0" && (
                    <div
                      className={`flex items-center gap-1 font-mono text-xs ${
                        stat.change.startsWith("+") ? "text-success" : "text-danger"
                      }`}
                    >
                      <TrendingUpIcon size={14} />
                      {stat.change}
                    </div>
                  )}
                </div>
                <p className="font-display text-2xl font-bold text-primary">{stat.value}</p>
                <p className="font-mono text-sm text-muted">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Plugins Section */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-primary">Your Plugins</h2>
              <p className="text-sm text-muted">{userPlugins.length} plugins total</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* View Toggle - Hidden on small screens, using grid by default */}
              <div className="hidden rounded-lg border border-elevated bg-surface/50 p-1 sm:flex">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`flex items-center justify-center rounded-md p-2 transition-colors ${
                    viewMode === "grid"
                      ? "bg-elevated text-primary"
                      : "text-muted hover:text-secondary"
                  }`}
                >
                  <GridIcon size={18} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center justify-center rounded-md p-2 transition-colors ${
                    viewMode === "list"
                      ? "bg-elevated text-primary"
                      : "text-muted hover:text-secondary"
                  }`}
                >
                  <ListIcon size={18} />
                </button>
              </div>

              {/* Create New Button */}
              <button
                onClick={() => {
                  setShowUploadModal(true);
                  setInitialUploadMode("scratch");
                }}
                className="btn-primary flex-1 sm:flex-none"
              >
                <PlusIcon size={18} />
                <span className="sm:hidden">New</span>
                <span className="hidden sm:inline">Create New</span>
              </button>

              {/* Upload Button with Dropdown */}
              <div className="relative flex-1 sm:flex-none" ref={uploadDropdownRef}>
                <button
                  onClick={() => setShowUploadDropdown(!showUploadDropdown)}
                  className="btn-secondary w-full sm:w-auto"
                >
                  <UploadIcon size={18} />
                  Upload
                  <ChevronDownIcon
                    size={14}
                    className={`ml-1 transition-transform ${showUploadDropdown ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Hidden folder input */}
                <input
                  ref={folderInputRef}
                  type="file"
                  className="hidden"
                  {...({
                    webkitdirectory: "",
                    directory: "",
                  } as React.InputHTMLAttributes<HTMLInputElement>)}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setPendingUploadFiles(Array.from(e.target.files));
                      setShowUploadModal(true);
                      setInitialUploadMode("existing");
                    }
                    setShowUploadDropdown(false);
                  }}
                />

                {/* Dropdown Menu */}
                {showUploadDropdown && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-lg border border-elevated bg-surface shadow-xl">
                    <div className="p-1">
                      <button
                        onClick={() => {
                          folderInputRef.current?.click();
                        }}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-elevated"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/20">
                          <FolderIcon size={16} className="text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium text-secondary">From Folder</p>
                          <p className="text-xs text-muted">Select a local plugin folder</p>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setShowUploadDropdown(false);
                          setShowUploadModal(true);
                          setInitialUploadMode("github");
                        }}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-elevated"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-elevated">
                          <GitHubIcon size={16} className="text-secondary" />
                        </div>
                        <div>
                          <p className="font-medium text-secondary">From GitHub</p>
                          <p className="text-xs text-muted">Import from a repository URL</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Plugins Grid/List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                <p className="font-mono text-sm text-muted">Loading plugins...</p>
              </div>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
              {userPlugins.map((plugin) => (
                <div key={plugin.id} className="card-glow group p-6">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber/20 to-amber/20 ring-1 ring-amber/30">
                        <span className="font-display text-lg font-bold text-amber">
                          {plugin.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-primary">{plugin.name}</h3>
                        <p className="font-mono text-xs text-muted">
                          v{plugin.versions?.[0]?.version || "1.0.0"}
                        </p>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setStatusPopoverPluginId(
                            statusPopoverPluginId === plugin.id ? null : plugin.id
                          );
                        }}
                        className={`rounded-full px-2 py-1 text-xs ring-1 transition-all hover:ring-2 ${getStatusColor(plugin.status)} ${
                          plugin.status === "DRAFT" || plugin.status === "REJECTED"
                            ? "cursor-pointer"
                            : ""
                        }`}
                      >
                        {getStatusLabel(plugin.status)}
                      </button>
                      {statusPopoverPluginId === plugin.id && (
                        <div
                          ref={statusPopoverRef}
                          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-subtle bg-elevated p-4 shadow-xl"
                        >
                          {plugin.status === "DRAFT" && (
                            <>
                              <p className="mb-3 text-sm text-muted">
                                Ready to share your plugin? Submit it for review to get it
                                published.
                              </p>
                              {plugin.files && plugin.files.length === 0 ? (
                                <p className="text-xs text-warning">
                                  Add at least one file before submitting.
                                </p>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmitForReview(plugin.id);
                                  }}
                                  disabled={isSubmittingForReview}
                                  className="btn-primary w-full text-sm"
                                >
                                  {isSubmittingForReview ? "Submitting..." : "Submit for Review"}
                                </button>
                              )}
                            </>
                          )}
                          {plugin.status === "REJECTED" && (
                            <>
                              <p className="mb-2 text-xs font-medium text-danger">Feedback:</p>
                              <p className="mb-3 rounded bg-danger/10 p-2 text-sm text-muted">
                                {plugin.reviewNote || "No feedback provided"}
                              </p>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSubmitForReview(plugin.id);
                                }}
                                disabled={isSubmittingForReview}
                                className="btn-primary w-full text-sm"
                              >
                                {isSubmittingForReview ? "Submitting..." : "Resubmit for Review"}
                              </button>
                            </>
                          )}
                          {plugin.status === "PENDING_REVIEW" && (
                            <p className="text-sm text-muted">
                              Your plugin is being reviewed. We'll notify you when it's approved.
                            </p>
                          )}
                          {(plugin.status === "APPROVED" || plugin.status === "PUBLISHED") && (
                            <p className="text-sm text-success">
                              Your plugin is live and available to users.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="mb-3 line-clamp-2 text-sm text-muted">{plugin.description}</p>

                  {/* File Type Items - show one of each + count */}
                  {(() => {
                    const skills = plugin.files?.filter((f) => f.fileType === "SKILL") || [];
                    const commands = plugin.files?.filter((f) => f.fileType === "COMMAND") || [];
                    const agents = plugin.files?.filter((f) => f.fileType === "AGENT") || [];
                    const hooks = plugin.files?.filter((f) => f.fileType === "HOOK") || [];
                    const hasItems =
                      skills.length > 0 ||
                      commands.length > 0 ||
                      agents.length > 0 ||
                      hooks.length > 0;
                    if (!hasItems) return null;

                    return (
                      <div className="mb-3 flex flex-wrap items-center gap-1.5">
                        {skills.map((skill) => (
                          <span
                            key={skill.id}
                            className="flex items-center gap-1 rounded-full bg-purple-500/10 px-2 py-0.5 text-xs text-purple-400 ring-1 ring-purple-500/20"
                          >
                            <SparklesIcon size={10} />
                            {skill.displayName ||
                              skill.fileName.split("/").pop()?.replace(".md", "")}
                          </span>
                        ))}
                        {commands.map((command) => (
                          <span
                            key={command.id}
                            className="flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-400 ring-1 ring-blue-500/20"
                          >
                            <TerminalIcon size={10} />/{plugin.slug}:
                            {command.displayName?.toLowerCase().replace(/\s+/g, "-") ||
                              command.fileName.split("/").pop()?.replace(".md", "")}
                          </span>
                        ))}
                        {agents.map((agent) => (
                          <span
                            key={agent.id}
                            className="flex items-center gap-1 rounded-full bg-amber/10 px-2 py-0.5 text-xs text-amber ring-1 ring-amber/20"
                          >
                            <BotIcon size={10} />
                            {agent.displayName ||
                              agent.fileName.split("/").pop()?.replace(".md", "")}
                          </span>
                        ))}
                        {hooks.map((hook) => (
                          <span
                            key={hook.id}
                            className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs text-success ring-1 ring-success/20"
                          >
                            <ZapIcon size={10} />
                            {hook.displayName ||
                              hook.fileName.split("/").pop()?.replace(".json", "")}
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  <div className="mb-4 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5 text-muted">
                      <DownloadIcon size={14} />
                      <span className="font-mono">
                        {formatNumber(plugin._count?.downloads || 0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted">
                      <StarIcon size={14} className="text-amber" />
                      <span className="font-mono">{plugin._count?.favorites || 0}</span>
                    </div>
                    {plugin.pricingModel !== "FREE" && plugin.price && (
                      <span className="ml-auto rounded-full bg-success/10 px-2 py-0.5 font-mono text-xs font-medium text-success">
                        ${(plugin.price / 100).toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 border-t border-elevated pt-4">
                    <Link
                      to={`/plugin/${plugin.slug}`}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-elevated/50 px-3 py-2 text-sm text-muted transition-colors hover:bg-elevated hover:text-secondary"
                    >
                      <EyeIcon size={14} />
                      View
                    </Link>
                    {(plugin.status === "APPROVED" || plugin.status === "PUBLISHED") && (
                      <button
                        onClick={() => openPricingModal(plugin)}
                        title="Set price"
                        className="flex items-center justify-center rounded-lg bg-elevated/50 p-2 text-muted transition-colors hover:bg-success/20 hover:text-success"
                      >
                        <TrendingUpIcon size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => copyPluginUrl(plugin.slug, plugin.id)}
                      title="Copy plugin URL"
                      className="flex items-center justify-center rounded-lg bg-elevated/50 p-2 text-muted transition-colors hover:bg-amber/20 hover:text-amber"
                    >
                      {copiedPluginId === plugin.id ? (
                        <CheckIcon size={14} />
                      ) : (
                        <LinkIcon size={14} />
                      )}
                    </button>
                    <button
                      onClick={() => canDeletePlugin(plugin.status) && setDeletePluginId(plugin.id)}
                      disabled={!canDeletePlugin(plugin.status)}
                      title={
                        canDeletePlugin(plugin.status)
                          ? "Delete plugin"
                          : "Only draft or rejected plugins can be deleted"
                      }
                      className={`flex items-center justify-center rounded-lg bg-elevated/50 p-2 transition-colors ${
                        canDeletePlugin(plugin.status)
                          ? "text-muted hover:bg-danger/20 hover:text-danger"
                          : "cursor-not-allowed text-muted/50"
                      }`}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Mobile: Show grid even when list is selected */}
              <div className="grid gap-4 sm:hidden">
                {userPlugins.map((plugin) => (
                  <div key={plugin.id} className="card-glow group p-4">
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber/20 to-amber/20 ring-1 ring-amber/30">
                          <span className="font-display font-bold text-amber">
                            {plugin.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-primary">{plugin.name}</h3>
                          <p className="font-mono text-xs text-muted">
                            v{plugin.versions?.[0]?.version || "1.0.0"}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ring-1 ${getStatusColor(plugin.status)}`}
                      >
                        {getStatusLabel(plugin.status)}
                      </span>
                    </div>
                    <p className="mb-3 line-clamp-2 text-sm text-muted">{plugin.description}</p>
                    <div className="flex items-center gap-2 border-t border-elevated pt-3">
                      <Link
                        to={`/plugin/${plugin.slug}`}
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-elevated/50 px-3 py-2 text-sm text-muted transition-colors hover:bg-elevated hover:text-secondary"
                      >
                        <EyeIcon size={14} />
                        View
                      </Link>
                      <button
                        onClick={() => copyPluginUrl(plugin.slug, plugin.id)}
                        className="flex items-center justify-center rounded-lg bg-elevated/50 p-2 text-muted transition-colors hover:bg-amber/20 hover:text-amber"
                      >
                        {copiedPluginId === plugin.id ? (
                          <CheckIcon size={14} />
                        ) : (
                          <LinkIcon size={14} />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          canDeletePlugin(plugin.status) && setDeletePluginId(plugin.id)
                        }
                        disabled={!canDeletePlugin(plugin.status)}
                        className={`flex items-center justify-center rounded-lg bg-elevated/50 p-2 transition-colors ${
                          canDeletePlugin(plugin.status)
                            ? "text-muted hover:bg-danger/20 hover:text-danger"
                            : "cursor-not-allowed text-muted/50"
                        }`}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Show table */}
              <div className="card-glow hidden overflow-hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-elevated text-left">
                      <th className="px-6 py-4 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Plugin
                      </th>
                      <th className="px-6 py-4 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Status
                      </th>
                      <th className="px-6 py-4 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Downloads
                      </th>
                      <th className="px-6 py-4 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Files
                      </th>
                      <th className="px-6 py-4 font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Updated
                      </th>
                      <th className="px-6 py-4 text-right font-mono text-xs font-medium uppercase tracking-wider text-muted">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface">
                    {userPlugins.map((plugin) => (
                      <tr key={plugin.id} className="group transition-colors hover:bg-surface/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber/20 to-amber/20">
                              <span className="font-display font-bold text-amber">
                                {plugin.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-display font-medium text-primary">{plugin.name}</p>
                              <p className="font-mono text-xs text-muted">
                                v{plugin.versions?.[0]?.version || "1.0.0"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative inline-block">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setStatusPopoverPluginId(
                                  statusPopoverPluginId === plugin.id ? null : plugin.id
                                );
                              }}
                              className={`inline-flex rounded-full px-2 py-1 text-xs ring-1 transition-all hover:ring-2 ${getStatusColor(plugin.status)} ${
                                plugin.status === "DRAFT" || plugin.status === "REJECTED"
                                  ? "cursor-pointer"
                                  : ""
                              }`}
                            >
                              {getStatusLabel(plugin.status)}
                            </button>
                            {statusPopoverPluginId === plugin.id && (
                              <div
                                ref={statusPopoverRef}
                                className="absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border border-subtle bg-elevated p-4 shadow-xl"
                              >
                                {plugin.status === "DRAFT" && (
                                  <>
                                    <p className="mb-3 text-sm text-muted">
                                      Ready to share your plugin? Submit it for review to get it
                                      published.
                                    </p>
                                    {plugin.files && plugin.files.length === 0 ? (
                                      <p className="text-xs text-warning">
                                        Add at least one file before submitting.
                                      </p>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleSubmitForReview(plugin.id);
                                        }}
                                        disabled={isSubmittingForReview}
                                        className="btn-primary w-full text-sm"
                                      >
                                        {isSubmittingForReview
                                          ? "Submitting..."
                                          : "Submit for Review"}
                                      </button>
                                    )}
                                  </>
                                )}
                                {plugin.status === "REJECTED" && (
                                  <>
                                    <p className="mb-2 text-xs font-medium text-danger">
                                      Feedback:
                                    </p>
                                    <p className="mb-3 rounded bg-danger/10 p-2 text-sm text-muted">
                                      {plugin.reviewNote || "No feedback provided"}
                                    </p>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSubmitForReview(plugin.id);
                                      }}
                                      disabled={isSubmittingForReview}
                                      className="btn-primary w-full text-sm"
                                    >
                                      {isSubmittingForReview
                                        ? "Submitting..."
                                        : "Resubmit for Review"}
                                    </button>
                                  </>
                                )}
                                {plugin.status === "PENDING_REVIEW" && (
                                  <p className="text-sm text-muted">
                                    Your plugin is being reviewed. We'll notify you when it's
                                    approved.
                                  </p>
                                )}
                                {(plugin.status === "APPROVED" ||
                                  plugin.status === "PUBLISHED") && (
                                  <p className="text-sm text-success">
                                    Your plugin is live and available to users.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-muted">
                          {formatNumber(plugin._count?.downloads || 0)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap items-center gap-1">
                            {(() => {
                              const skills =
                                plugin.files?.filter((f) => f.fileType === "SKILL") || [];
                              const commands =
                                plugin.files?.filter((f) => f.fileType === "COMMAND") || [];
                              const agents =
                                plugin.files?.filter((f) => f.fileType === "AGENT") || [];
                              const hooks =
                                plugin.files?.filter((f) => f.fileType === "HOOK") || [];
                              return (
                                <>
                                  {skills.map((skill) => (
                                    <span
                                      key={skill.id}
                                      className="flex items-center gap-0.5 rounded bg-purple-500/10 px-1.5 py-0.5 text-xs text-purple-400"
                                    >
                                      <SparklesIcon size={10} />
                                      {skill.displayName ||
                                        skill.fileName.split("/").pop()?.replace(".md", "")}
                                    </span>
                                  ))}
                                  {commands.map((command) => (
                                    <span
                                      key={command.id}
                                      className="flex items-center gap-0.5 rounded bg-blue-500/10 px-1.5 py-0.5 text-xs text-blue-400"
                                    >
                                      <TerminalIcon size={10} />/{plugin.slug}:
                                      {command.displayName?.toLowerCase().replace(/\s+/g, "-") ||
                                        command.fileName.split("/").pop()?.replace(".md", "")}
                                    </span>
                                  ))}
                                  {agents.map((agent) => (
                                    <span
                                      key={agent.id}
                                      className="flex items-center gap-0.5 rounded bg-amber/10 px-1.5 py-0.5 text-xs text-amber"
                                    >
                                      <BotIcon size={10} />
                                      {agent.displayName ||
                                        agent.fileName.split("/").pop()?.replace(".md", "")}
                                    </span>
                                  ))}
                                  {hooks.map((hook) => (
                                    <span
                                      key={hook.id}
                                      className="flex items-center gap-0.5 rounded bg-success/10 px-1.5 py-0.5 text-xs text-success"
                                    >
                                      <ZapIcon size={10} />
                                      {hook.displayName ||
                                        hook.fileName.split("/").pop()?.replace(".json", "")}
                                    </span>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-muted">
                          {new Date(plugin.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/plugin/${plugin.slug}`}
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-elevated hover:text-secondary"
                              title="View plugin"
                            >
                              <EyeIcon size={16} />
                            </Link>
                            <button
                              onClick={() => copyPluginUrl(plugin.slug, plugin.id)}
                              title="Copy plugin URL"
                              className="rounded-lg p-2 text-muted transition-colors hover:bg-amber/20 hover:text-amber"
                            >
                              {copiedPluginId === plugin.id ? (
                                <CheckIcon size={16} />
                              ) : (
                                <LinkIcon size={16} />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                canDeletePlugin(plugin.status) && setDeletePluginId(plugin.id)
                              }
                              disabled={!canDeletePlugin(plugin.status)}
                              title={
                                canDeletePlugin(plugin.status)
                                  ? "Delete plugin"
                                  : "Only draft or rejected plugins can be deleted"
                              }
                              className={`rounded-lg p-2 transition-colors ${
                                canDeletePlugin(plugin.status)
                                  ? "text-muted hover:bg-danger/20 hover:text-danger"
                                  : "cursor-not-allowed text-muted/50"
                              }`}
                            >
                              <TrashIcon size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadPluginModal
          onClose={() => {
            setShowUploadModal(false);
            setInitialUploadMode(null);
            setPendingUploadFiles([]);
          }}
          onSuccess={() => {
            loadPlugins();
          }}
          onPluginCreated={(pluginSlug) => {
            setShowUploadModal(false);
            setInitialUploadMode(null);
            setPendingUploadFiles([]);
            loadPlugins();
            navigate(`/plugin/${pluginSlug}`);
          }}
          initialMode={initialUploadMode}
          pendingFiles={pendingUploadFiles}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletePluginId && pluginToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-base/80 backdrop-blur-sm"
            onClick={() => setDeletePluginId(null)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-elevated bg-surface p-6 shadow-2xl">
            <h3 className="font-display text-xl font-bold text-primary">Delete Plugin</h3>
            <p className="mt-2 text-muted">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-secondary">{pluginToDelete.name}</span>? This
              action cannot be undone.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={handleDeletePlugin}
                disabled={isDeleting}
                className="flex-1 rounded-lg bg-danger px-4 py-2 font-medium text-white transition-colors hover:bg-danger/90 disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setDeletePluginId(null)}
                disabled={isDeleting}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {pricingPluginId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-base/80 backdrop-blur-sm"
            onClick={() => setPricingPluginId(null)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-elevated bg-surface p-6 shadow-2xl">
            <h3 className="font-display text-xl font-bold text-primary">Set Plugin Price</h3>
            <p className="mt-2 text-sm text-muted">
              Choose a pricing model for your plugin. You keep 85% of each sale.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block font-mono text-xs text-muted">Pricing Model</label>
                <select
                  value={pricingModel}
                  onChange={(e) => setPricingModel(e.target.value)}
                  className="w-full rounded-lg border border-elevated bg-base px-3 py-2 font-mono text-sm text-primary"
                >
                  <option value="FREE">Free</option>
                  <option value="ONE_TIME">One-time Purchase</option>
                </select>
              </div>

              {pricingModel !== "FREE" && (
                <div>
                  <label className="mb-1 block font-mono text-xs text-muted">Price (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted">
                      $
                    </span>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      placeholder="5.00"
                      className="w-full rounded-lg border border-elevated bg-base py-2 pl-7 pr-3 font-mono text-sm text-primary"
                    />
                  </div>
                  <p className="mt-1 font-mono text-xs text-muted">Minimum $1.00</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={handleSavePrice}
                disabled={
                  isSavingPrice ||
                  (pricingModel !== "FREE" && (!priceInput || parseFloat(priceInput) < 1))
                }
                className="flex-1 rounded-lg bg-amber px-4 py-2 font-medium text-black transition-colors hover:bg-amber-hover disabled:opacity-50"
              >
                {isSavingPrice ? "Saving..." : "Save Price"}
              </button>
              <button
                onClick={() => setPricingPluginId(null)}
                disabled={isSavingPrice}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type UploadMode = "choose" | "scratch" | "existing";
type WizardStep = "metadata" | "files" | "review";
type ComponentType = "SKILL" | "COMMAND" | "AGENT" | "HOOK";
type FileAddMode = "select" | "create" | "upload";
type CreateStep = "form" | "preview";

const FILE_TYPE_OPTIONS: { value: PluginFile["fileType"]; label: string; description: string }[] = [
  { value: "SKILL", label: "Skill", description: "Markdown skill files (.md)" },
  { value: "COMMAND", label: "Command", description: "Slash command files (.md)" },
  { value: "AGENT", label: "Agent", description: "Agent definition files (.md)" },
  { value: "HOOK", label: "Hook", description: "Hook configuration files (.json)" },
  { value: "MCP_CONFIG", label: "MCP Config", description: "MCP server configuration (.json)" },
  { value: "OTHER", label: "Other", description: "Other plugin files" },
];

const COMPONENT_TYPES: {
  type: ComponentType;
  label: string;
  icon: string;
  description: string;
  color: string;
}[] = [
  {
    type: "SKILL",
    label: "Skill",
    icon: "⚡",
    description: "Teach Claude new capabilities with markdown instructions",
    color: "purple",
  },
  {
    type: "COMMAND",
    label: "Command",
    icon: "/",
    description: "Create slash commands for quick actions",
    color: "blue",
  },
  {
    type: "AGENT",
    label: "Agent",
    icon: "🤖",
    description: "Define specialized subagents for complex tasks",
    color: "amber",
  },
  {
    type: "HOOK",
    label: "Hook",
    icon: "🪝",
    description: "Add automation triggers for tool events",
    color: "success",
  },
];

const AVAILABLE_TOOLS = [
  "Read",
  "Write",
  "Edit",
  "Glob",
  "Grep",
  "Bash",
  "Task",
  "WebFetch",
  "WebSearch",
  "TodoWrite",
  "AskUserQuestion",
];

const HOOK_EVENTS = [
  { value: "PreToolUse", label: "PreToolUse", description: "Before Claude uses a tool" },
  { value: "PostToolUse", label: "PostToolUse", description: "After Claude uses a tool" },
  { value: "Stop", label: "Stop", description: "When main agent finishes" },
  { value: "SubagentStop", label: "SubagentStop", description: "When subagent finishes" },
  { value: "SessionStart", label: "SessionStart", description: "When session starts" },
  { value: "UserPromptSubmit", label: "UserPromptSubmit", description: "When user submits prompt" },
];

function detectFileType(filePath: string): PluginFile["fileType"] {
  const lowerPath = filePath.toLowerCase();
  const fileName = lowerPath.split("/").pop() || "";
  const pathParts = lowerPath.split("/");

  // Check for special file names first
  // Skills are ONLY files named skill.md (case insensitive)
  if (fileName === "skill.md") return "SKILL";
  if (fileName === "hooks.json") return "HOOK";
  if (fileName.endsWith(".mcp.json") || fileName === "mcp.json") return "MCP_CONFIG";

  // Commands: .md files directly in commands/ (e.g., "commands/deploy.md")
  // Not nested files like "commands/subfolder/helper.md"
  if (pathParts[0] === "commands" && pathParts.length === 2 && fileName.endsWith(".md")) {
    return "COMMAND";
  }

  // Agents: .md files directly in agents/ (e.g., "agents/reviewer.md")
  // Not nested files like "agents/subfolder/helper.md"
  if (pathParts[0] === "agents" && pathParts.length === 2 && fileName.endsWith(".md")) {
    return "AGENT";
  }

  // Hooks directory (any file in hooks/ is a hook)
  if (pathParts[0] === "hooks") {
    return "HOOK";
  }

  return "OTHER";
}

// Component creation form data types
interface SkillFormData {
  name: string;
  description: string;
  allowedTools: string[];
  model: string;
  content: string;
}

interface CommandFormData {
  name: string;
  description: string;
  argumentHint: string;
  allowedTools: string[];
  model: string;
  content: string;
}

interface AgentFormData {
  name: string;
  description: string;
  tools: string[];
  model: string;
  permissionMode: string;
  skills: string;
  content: string;
}

interface HookFormData {
  event: string;
  matcher: string;
  hookType: "command" | "prompt";
  command: string;
  prompt: string;
  timeout: number;
}

// Generate file content from form data
function generateSkillContent(data: SkillFormData): string {
  const frontmatter = ["---", `name: ${data.name}`, `description: ${data.description}`];
  if (data.allowedTools.length > 0) {
    frontmatter.push(`allowed-tools: ${data.allowedTools.join(", ")}`);
  }
  if (data.model) {
    frontmatter.push(`model: ${data.model}`);
  }
  frontmatter.push("---", "");

  return frontmatter.join("\n") + data.content;
}

function generateCommandContent(data: CommandFormData): string {
  const frontmatter = ["---"];
  if (data.description) {
    frontmatter.push(`description: ${data.description}`);
  }
  if (data.argumentHint) {
    frontmatter.push(`argument-hint: ${data.argumentHint}`);
  }
  if (data.allowedTools.length > 0) {
    frontmatter.push(`allowed-tools: ${data.allowedTools.join(", ")}`);
  }
  if (data.model) {
    frontmatter.push(`model: ${data.model}`);
  }
  frontmatter.push("---", "");

  return frontmatter.join("\n") + data.content;
}

function generateAgentContent(data: AgentFormData): string {
  const frontmatter = ["---", `name: ${data.name}`, `description: ${data.description}`];
  if (data.tools.length > 0) {
    frontmatter.push(`tools: ${data.tools.join(", ")}`);
  }
  if (data.model) {
    frontmatter.push(`model: ${data.model}`);
  }
  if (data.permissionMode && data.permissionMode !== "default") {
    frontmatter.push(`permissionMode: ${data.permissionMode}`);
  }
  if (data.skills) {
    frontmatter.push(`skills: ${data.skills}`);
  }
  frontmatter.push("---", "");

  return frontmatter.join("\n") + data.content;
}

function generateHookContent(data: HookFormData): string {
  const hook: Record<string, unknown> = {
    type: data.hookType,
    timeout: data.timeout,
  };
  if (data.hookType === "command") {
    hook.command = data.command;
  } else {
    hook.prompt = data.prompt;
  }

  const config: Record<string, unknown> = {
    hooks: {
      [data.event]: [
        {
          ...(data.matcher && { matcher: data.matcher }),
          hooks: [hook],
        },
      ],
    },
  };

  return JSON.stringify(config, null, 2);
}

function UploadPluginModal({
  onClose,
  onSuccess,
  onPluginCreated,
  initialMode,
  pendingFiles,
}: {
  onClose: () => void;
  onSuccess: () => void;
  onPluginCreated: (pluginSlug: string) => void;
  initialMode?: "scratch" | "existing" | "github" | null;
  pendingFiles?: File[];
}) {
  // Set initial mode based on prop
  const [mode, setMode] = useState<UploadMode>(
    initialMode === "scratch" || initialMode === "existing"
      ? "scratch"
      : initialMode === "github"
        ? "existing" // github starts at existing mode but shows github step
        : "choose"
  );
  const [step, setStep] = useState<WizardStep>(
    initialMode === "github" ? "metadata" : "metadata" // Always start at metadata for now
  );

  // GitHub import state
  const [showGithubInput, setShowGithubInput] = useState(initialMode === "github");
  const [githubUrl, setGithubUrl] = useState("");
  const [isFetchingGithub, setIsFetchingGithub] = useState(false);
  const [popularTags, setPopularTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [plugin, setPlugin] = useState<Plugin | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pending upload state - replaces window globals
  const [pendingUpload, setPendingUpload] = useState<{
    files: File[];
    paths: string[];
    pluginRoot: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tags: [] as string[],
    version: "1.0.0",
    repositoryUrl: "",
  });

  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState<PluginFile["fileType"]>("SKILL");
  const [folderUploadProgress, setFolderUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  // Component creation wizard state
  const [fileAddMode, setFileAddMode] = useState<FileAddMode>("select");
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | null>(null);
  const [createStep, setCreateStep] = useState<CreateStep>("form");
  const [generatedContent, setGeneratedContent] = useState<string>("");
  const [generatedFileName, setGeneratedFileName] = useState<string>("");

  // Form data for each component type
  const [skillForm, setSkillForm] = useState<SkillFormData>({
    name: "",
    description: "",
    allowedTools: [],
    model: "",
    content:
      "# Your Skill Name\n\nDescribe what this skill does and how Claude should use it.\n\n## Instructions\n\n- Step 1\n- Step 2\n- Step 3\n",
  });

  const [commandForm, setCommandForm] = useState<CommandFormData>({
    name: "",
    description: "",
    argumentHint: "",
    allowedTools: [],
    model: "",
    content:
      "Your command instructions here.\n\nUse $ARGUMENTS for all arguments or $1, $2 for individual arguments.\n",
  });

  const [agentForm, setAgentForm] = useState<AgentFormData>({
    name: "",
    description: "",
    tools: [],
    model: "",
    permissionMode: "default",
    skills: "",
    content:
      "You are a specialized agent for [purpose].\n\nWhen invoked:\n1. First, understand the task\n2. Then, execute the required steps\n3. Finally, report your findings\n\n## Guidelines\n\n- Be thorough\n- Be concise\n- Follow best practices\n",
  });

  const [hookForm, setHookForm] = useState<HookFormData>({
    event: "PostToolUse",
    matcher: "",
    hookType: "command",
    command: "${CLAUDE_PLUGIN_ROOT}/scripts/my-hook.sh",
    prompt: "Evaluate the output and determine if the task was completed successfully.",
    timeout: 30,
  });

  const resetCreationWizard = () => {
    setFileAddMode("select");
    setSelectedComponentType(null);
    setCreateStep("form");
    setGeneratedContent("");
    setGeneratedFileName("");
  };

  const handleGeneratePreview = () => {
    if (!selectedComponentType) return;

    let content = "";
    let fileName = "";

    switch (selectedComponentType) {
      case "SKILL":
        content = generateSkillContent(skillForm);
        fileName = `skills/${skillForm.name}/SKILL.md`;
        break;
      case "COMMAND":
        content = generateCommandContent(commandForm);
        fileName = `commands/${commandForm.name}.md`;
        break;
      case "AGENT":
        content = generateAgentContent(agentForm);
        fileName = `agents/${agentForm.name}.md`;
        break;
      case "HOOK":
        content = generateHookContent(hookForm);
        fileName = "hooks/hooks.json";
        break;
    }

    setGeneratedContent(content);
    setGeneratedFileName(fileName);
    setCreateStep("preview");
  };

  const handleSaveComponent = async () => {
    if (!plugin || !generatedContent || !generatedFileName) return;

    setUploadingFile(true);
    setError(null);

    try {
      const blob = new Blob([generatedContent], { type: "text/plain" });
      const file = new File([blob], generatedFileName.split("/").pop() || "file.md", {
        type: "text/plain",
      });

      await pluginApi.uploadFile(
        plugin.id,
        file,
        selectedComponentType as PluginFile["fileType"],
        generatedFileName
      );
      const updated = await pluginApi.getPlugin(plugin.slug);
      setPlugin(updated);
      resetCreationWizard();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save component");
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    pluginApi.getPopularTags().then(setPopularTags).catch(console.error);
  }, []);

  // Handle initial mode with pending files (from Upload button)
  useEffect(() => {
    if (initialMode === "existing" && pendingFiles && pendingFiles.length > 0) {
      const processPendingFiles = async () => {
        // Look for plugin.json to extract metadata
        const pluginJsonFile = pendingFiles.find(
          (f) =>
            f.webkitRelativePath?.endsWith("plugin.json") ||
            f.webkitRelativePath?.includes(".claude-plugin/plugin.json") ||
            f.name === "plugin.json"
        );

        let pluginName = "";
        let pluginDescription = "";
        let pluginVersion = "1.0.0";
        let pluginKeywords: string[] = [];

        if (pluginJsonFile) {
          try {
            const text = await pluginJsonFile.text();
            const pluginJson = JSON.parse(text);
            pluginName = pluginJson.name || "";
            pluginDescription = pluginJson.description || "";
            pluginVersion = pluginJson.version || "1.0.0";
            // Extract keywords and map to tags
            if (Array.isArray(pluginJson.keywords)) {
              pluginKeywords = pluginJson.keywords
                .filter((k: unknown) => typeof k === "string")
                .map((k: string) => k.toLowerCase().trim())
                .filter((k: string) => k.length > 0 && k.length <= 30)
                .slice(0, 10);
            }
          } catch {
            // Ignore parse errors
          }
        }

        // Pre-fill form with detected values
        if (pluginName || pluginKeywords.length > 0) {
          setFormData((prev) => ({
            ...prev,
            name: pluginName || prev.name,
            description: pluginDescription || prev.description,
            version: pluginVersion,
            tags: pluginKeywords.length > 0 ? pluginKeywords : prev.tags,
          }));
        }

        // Filter out common non-plugin directories
        const validFiles = pendingFiles.filter((f) => {
          const path = (f.webkitRelativePath || f.name).toLowerCase();
          return !path.includes("node_modules/") && !path.includes(".git/");
        });

        if (validFiles.length > 0) {
          // Calculate plugin root from file paths
          const paths = validFiles.map((f) => f.webkitRelativePath || f.name);
          const pluginJsonPath = paths.find((p) => p.endsWith(".claude-plugin/plugin.json"));
          let pluginRoot = "";
          if (pluginJsonPath) {
            const idx = pluginJsonPath.lastIndexOf(".claude-plugin/plugin.json");
            pluginRoot = pluginJsonPath.slice(0, idx);
          }

          setPendingUpload({
            files: validFiles,
            paths,
            pluginRoot,
          });
        }
      };

      processPendingFiles();
    }
  }, [initialMode, pendingFiles]);

  // Parse GitHub URL to extract owner, repo, and optional path
  const parseGithubUrl = (
    url: string
  ): { owner: string; repo: string; branch?: string; path?: string } | null => {
    try {
      // Handle various GitHub URL formats
      // https://github.com/owner/repo
      // https://github.com/owner/repo/tree/branch
      // https://github.com/owner/repo/tree/branch/path/to/folder
      const urlObj = new URL(url);
      if (!urlObj.hostname.includes("github.com")) return null;

      const pathParts = urlObj.pathname.split("/").filter(Boolean);
      if (pathParts.length < 2) return null;

      const owner = pathParts[0];
      const repo = pathParts[1];

      // Check for /tree/branch/path format
      if (pathParts[2] === "tree" && pathParts.length >= 4) {
        const branch = pathParts[3];
        const path = pathParts.length > 4 ? pathParts.slice(4).join("/") : undefined;
        return { owner, repo, branch, path };
      }

      return { owner, repo };
    } catch {
      return null;
    }
  };

  // Fetch repository contents via backend proxy (avoids GitHub API rate limits)
  const handleGithubImport = async () => {
    // Basic client-side validation
    const parsed = parseGithubUrl(githubUrl);
    if (!parsed) {
      setError("Invalid GitHub URL. Please use a URL like: https://github.com/owner/repo");
      return;
    }

    setIsFetchingGithub(true);
    setError(null);

    try {
      // Use backend proxy to fetch repository contents
      const result = await pluginApi.fetchGithubRepo(githubUrl);
      const files = result.files;

      if (files.length === 0) {
        throw new Error("No valid plugin files found in repository");
      }

      // Pre-fill form with plugin.json metadata if available
      if (result.pluginJson) {
        // Extract keywords and map to tags
        let pluginKeywords: string[] = [];
        if (Array.isArray(result.pluginJson.keywords)) {
          pluginKeywords = result.pluginJson.keywords
            .filter((k: unknown) => typeof k === "string")
            .map((k: string) => k.toLowerCase().trim())
            .filter((k: string) => k.length > 0 && k.length <= 30)
            .slice(0, 10);
        }
        setFormData((prev) => ({
          ...prev,
          name: result.pluginJson?.name || prev.name,
          description: result.pluginJson?.description || prev.description,
          version: result.pluginJson?.version || prev.version,
          repositoryUrl: githubUrl,
          tags: pluginKeywords.length > 0 ? pluginKeywords : prev.tags,
        }));
      } else {
        // Pre-fill repository URL at least
        setFormData((prev) => ({ ...prev, repositoryUrl: githubUrl }));
      }

      // Convert to File objects and store for later upload
      const fileObjects = files.map((f) => {
        const blob = new Blob([f.content], { type: "text/plain" });
        return new File([blob], f.path.split("/").pop() || "file", { type: "text/plain" });
      });

      // Store files with paths and plugin root for upload after plugin creation
      setPendingUpload({
        files: fileObjects,
        paths: files.map((f) => f.path),
        pluginRoot: result.pluginRoot,
      });

      setShowGithubInput(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to import from GitHub");
    } finally {
      setIsFetchingGithub(false);
    }
  };

  const handleCreatePlugin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const created = await pluginApi.createPlugin({
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        version: formData.version,
        repositoryUrl: formData.repositoryUrl || undefined,
      });

      // If there are pending files from folder/GitHub upload, upload them first
      if (pendingUpload && pendingUpload.files.length > 0) {
        await uploadPendingFiles(created);

        // Check if .claude-plugin/plugin.json was included, create one if not
        const hasPluginJson = pendingUpload.paths.some(
          (p) =>
            p.endsWith(".claude-plugin/plugin.json") ||
            (pendingUpload.pluginRoot &&
              p === pendingUpload.pluginRoot + ".claude-plugin/plugin.json")
        );

        if (!hasPluginJson) {
          // Create plugin.json from form data
          // Convert name to kebab-case (required by Claude Code)
          const kebabName = formData.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
          const pluginJsonContent = JSON.stringify(
            {
              name: kebabName,
              description: formData.description,
              version: formData.version,
              keywords: formData.tags,
            },
            null,
            2
          );
          const pluginJsonBlob = new Blob([pluginJsonContent], { type: "application/json" });
          const pluginJsonFile = new File([pluginJsonBlob], "plugin.json", {
            type: "application/json",
          });

          try {
            await pluginApi.uploadFile(
              created.id,
              pluginJsonFile,
              "OTHER",
              ".claude-plugin/plugin.json"
            );
          } catch (err) {
            console.error("Failed to create plugin.json:", err);
          }
        }
      }

      // Navigate to the plugin detail page
      onPluginCreated(created.slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create plugin");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !plugin) return;

    const file = e.target.files[0];
    setUploadingFile(true);
    setError(null);

    try {
      await pluginApi.uploadFile(plugin.id, file, selectedFileType);
      // Refresh plugin to get updated files list
      const updated = await pluginApi.getPlugin(plugin.slug);
      setPlugin(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setUploadingFile(false);
      e.target.value = "";
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!plugin) return;

    try {
      await pluginApi.deleteFile(plugin.id, fileId);
      const updated = await pluginApi.getPlugin(plugin.slug);
      setPlugin(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete file");
    }
  };

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const files = Array.from(e.target.files);

    // Look for plugin.json to extract metadata
    const pluginJsonFile = files.find(
      (f) =>
        f.webkitRelativePath.endsWith("plugin.json") ||
        f.webkitRelativePath.includes(".claude-plugin/plugin.json")
    );

    let pluginName = "";
    let pluginDescription = "";
    let pluginVersion = "1.0.0";

    if (pluginJsonFile) {
      try {
        const text = await pluginJsonFile.text();
        const pluginJson = JSON.parse(text);
        pluginName = pluginJson.name || "";
        pluginDescription = pluginJson.description || "";
        pluginVersion = pluginJson.version || "1.0.0";
      } catch {
        // Ignore parse errors
      }
    }

    // Pre-fill form with detected values
    if (pluginName) {
      setFormData((prev) => ({
        ...prev,
        name: pluginName,
        description: pluginDescription || prev.description,
        version: pluginVersion,
      }));
    }

    // Filter out common non-plugin directories
    const pluginFiles = files.filter((f) => {
      const path = f.webkitRelativePath.toLowerCase();
      return !path.includes("node_modules/") && !path.includes(".git/");
    });

    if (pluginFiles.length === 0) {
      setError("No files found in folder");
      return;
    }

    // Calculate plugin root from file paths
    const paths = pluginFiles.map((f) => f.webkitRelativePath);
    const pluginJsonPath = paths.find((p) => p.endsWith(".claude-plugin/plugin.json"));
    let pluginRoot = "";
    if (pluginJsonPath) {
      const idx = pluginJsonPath.lastIndexOf(".claude-plugin/plugin.json");
      pluginRoot = pluginJsonPath.slice(0, idx);
    }

    // Store files for later upload after plugin creation
    setPendingUpload({
      files: pluginFiles,
      paths,
      pluginRoot,
    });
    setMode("scratch");
    setStep("metadata");
  };

  const uploadPendingFiles = async (createdPlugin: Plugin) => {
    if (!pendingUpload || pendingUpload.files.length === 0) return;

    const { files, paths, pluginRoot } = pendingUpload;

    // Build files to upload, stripping the plugin root prefix if needed
    const filesToUpload: { file: File; path: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const relativePath = paths[i];

      // Only include files under the plugin root (if there is one)
      if (pluginRoot && !relativePath.startsWith(pluginRoot)) {
        continue;
      }

      // Strip the plugin root prefix
      const cleanPath = pluginRoot ? relativePath.slice(pluginRoot.length) : relativePath;
      filesToUpload.push({ file, path: cleanPath });
    }

    setFolderUploadProgress({ current: 0, total: filesToUpload.length });

    for (let i = 0; i < filesToUpload.length; i++) {
      const { file, path: cleanPath } = filesToUpload[i];
      const fileType = detectFileType(cleanPath);

      try {
        await pluginApi.uploadFile(createdPlugin.id, file, fileType, cleanPath);
        setFolderUploadProgress({ current: i + 1, total: filesToUpload.length });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }

    setPendingUpload(null);
    setFolderUploadProgress(null);

    // Refresh plugin data
    const updated = await pluginApi.getPlugin(createdPlugin.slug);
    setPlugin(updated);
  };

  const handleSubmitForReview = async () => {
    if (!plugin) return;

    setIsLoading(true);
    setError(null);

    try {
      await pluginApi.submitForReview(plugin.id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit for review");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: Plugin["status"]) => {
    switch (status) {
      case "DRAFT":
        return "Draft";
      case "PENDING_REVIEW":
        return "Pending Review";
      case "APPROVED":
        return "Approved";
      case "REJECTED":
        return "Rejected";
      case "PUBLISHED":
        return "Published";
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-base/80 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-2xl border border-elevated bg-surface shadow-2xl">
        {/* Header */}
        <div className="border-b border-elevated p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">
                {mode === "choose" && "Create New Plugin"}
                {mode !== "choose" && step === "metadata" && "Create New Plugin"}
                {mode !== "choose" && step === "files" && "Upload Plugin Files"}
                {mode !== "choose" && step === "review" && "Review & Submit"}
              </h2>
              <p className="mt-1 text-muted">
                {mode === "choose" && "Choose how you want to create your plugin"}
                {mode !== "choose" &&
                  step === "metadata" &&
                  "Enter your plugin details to get started"}
                {mode !== "choose" && step === "files" && "Add skills, commands, agents, and hooks"}
                {mode !== "choose" && step === "review" && "Review your plugin before submission"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-muted hover:bg-elevated hover:text-secondary"
            >
              <XIcon size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
              {error}
            </div>
          )}

          {/* Upload progress */}
          {folderUploadProgress && (
            <div className="mb-4 rounded-lg border border-amber/30 bg-amber/10 p-4">
              <p className="mb-2 font-mono text-sm text-amber">
                Uploading files... {folderUploadProgress.current} / {folderUploadProgress.total}
              </p>
              <div className="h-2 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all"
                  style={{
                    width: `${(folderUploadProgress.current / folderUploadProgress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* GitHub Import Step */}
          {showGithubInput && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-elevated">
                  <GitHubIcon size={20} className="text-secondary" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-primary">Import from GitHub</h3>
                  <p className="text-sm text-muted">Enter the repository URL</p>
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-sm text-muted">
                  GitHub Repository URL
                </label>
                <input
                  type="url"
                  value={githubUrl}
                  onChange={(e) => setGithubUrl(e.target.value)}
                  placeholder="https://github.com/username/my-plugin"
                  className="input-field"
                  autoFocus
                />
                <p className="mt-2 font-mono text-xs text-muted">
                  Supports: github.com/owner/repo or github.com/owner/repo/tree/branch/path
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleGithubImport}
                  disabled={!githubUrl || isFetchingGithub}
                  className="btn-primary flex-1"
                >
                  {isFetchingGithub ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-base border-t-transparent" />
                      Fetching...
                    </>
                  ) : (
                    "Import Repository"
                  )}
                </button>
                <button onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Mode Selection */}
          {mode === "choose" && !showGithubInput && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setMode("scratch");
                  setStep("metadata");
                }}
                className="card-glow w-full p-6 text-left transition-all hover:border-amber/50"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber/20">
                    <PlusIcon size={24} className="text-amber" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-primary">
                      Create from Scratch
                    </h3>
                    <p className="mt-1 text-sm text-muted">
                      Start with a blank plugin and add files one by one
                    </p>
                  </div>
                </div>
              </button>

              <label className="card-glow block w-full cursor-pointer p-6 text-left transition-all hover:border-amber/50">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20">
                    <UploadIcon size={24} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-primary">
                      Upload Existing Plugin
                    </h3>
                    <p className="mt-1 text-sm text-muted">
                      Select your plugin folder to upload all files at once
                    </p>
                    <p className="mt-2 font-mono text-xs text-muted">
                      We'll detect plugin.json and auto-fill details
                    </p>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  {...({
                    webkitdirectory: "",
                    directory: "",
                  } as React.InputHTMLAttributes<HTMLInputElement>)}
                  onChange={handleFolderUpload}
                />
              </label>
            </div>
          )}

          {/* Step 1: Metadata */}
          {mode !== "choose" && step === "metadata" && !showGithubInput && (
            <form onSubmit={handleCreatePlugin} className="space-y-5">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label htmlFor="name" className="mb-2 block font-mono text-sm text-muted">
                    Plugin Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="my-awesome-plugin"
                    required
                    className="input-field"
                  />
                </div>
                <div className="w-28">
                  <label htmlFor="version" className="mb-2 block font-mono text-sm text-muted">
                    Version *
                  </label>
                  <input
                    id="version"
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0.0"
                    pattern="\d+\.\d+\.\d+"
                    required
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="mb-2 block font-mono text-sm text-muted">
                  Description *
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what your plugin does in detail..."
                  required
                  rows={3}
                  className="input-field resize-none"
                />
              </div>

              <div>
                <label htmlFor="tags" className="mb-2 block font-mono text-sm text-muted">
                  Tags
                </label>
                <div className="space-y-2">
                  {/* Display current tags */}
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 rounded-full bg-claude/20 px-3 py-1 font-mono text-sm text-claude"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() =>
                              setFormData({
                                ...formData,
                                tags: formData.tags.filter((t) => t !== tag),
                              })
                            }
                            className="ml-1 hover:text-red-400"
                          >
                            <XIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Tag input */}
                  <div className="flex gap-2">
                    <input
                      id="tags"
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const newTag = tagInput.trim().toLowerCase();
                          if (
                            newTag &&
                            !formData.tags.includes(newTag) &&
                            formData.tags.length < 10
                          ) {
                            setFormData({ ...formData, tags: [...formData.tags, newTag] });
                            setTagInput("");
                          }
                        }
                      }}
                      placeholder="Type a tag and press Enter"
                      className="input-field flex-1"
                      disabled={formData.tags.length >= 10}
                    />
                  </div>
                  {/* Popular tags suggestions */}
                  {popularTags.length > 0 && formData.tags.length < 10 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="font-mono text-xs text-muted">Suggestions:</span>
                      {popularTags
                        .filter((tag) => !formData.tags.includes(tag))
                        .slice(0, 8)
                        .map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              if (!formData.tags.includes(tag) && formData.tags.length < 10) {
                                setFormData({ ...formData, tags: [...formData.tags, tag] });
                              }
                            }}
                            className="rounded bg-elevated px-2 py-0.5 font-mono text-xs text-muted hover:bg-claude/20 hover:text-claude"
                          >
                            +{tag}
                          </button>
                        ))}
                    </div>
                  )}
                  <p className="font-mono text-xs text-muted">
                    {formData.tags.length}/10 tags • Press Enter or comma to add
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="repositoryUrl" className="mb-2 block font-mono text-sm text-muted">
                  Repository URL
                </label>
                <input
                  id="repositoryUrl"
                  type="url"
                  value={formData.repositoryUrl}
                  onChange={(e) => setFormData({ ...formData, repositoryUrl: e.target.value })}
                  placeholder="https://github.com/username/plugin"
                  className="input-field"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button type="submit" disabled={isLoading} className="btn-primary flex-1">
                  {isLoading ? "Creating..." : "Create Plugin"}
                </button>
                <button type="button" onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Files */}
          {mode !== "choose" && step === "files" && plugin && (
            <div className="space-y-6">
              {/* Mode Selection: Create or Upload */}
              {fileAddMode === "select" && (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      onClick={() => setFileAddMode("create")}
                      className="card-glow p-4 text-left transition-all hover:border-amber/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber/20 text-lg">
                          ✨
                        </div>
                        <div>
                          <h4 className="font-display font-bold text-primary">Create New</h4>
                          <p className="text-xs text-muted">
                            Build a skill, command, agent, or hook
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setFileAddMode("upload")}
                      className="card-glow p-4 text-left transition-all hover:border-purple-500/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/20">
                          <UploadIcon size={20} className="text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-display font-bold text-primary">Upload File</h4>
                          <p className="text-xs text-muted">Upload an existing file</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Component Type Selection */}
              {fileAddMode === "create" && !selectedComponentType && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-primary">
                      What do you want to create?
                    </h3>
                    <button
                      onClick={resetCreationWizard}
                      className="text-xs text-muted hover:text-secondary"
                    >
                      ← Back
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {COMPONENT_TYPES.map((comp) => (
                      <button
                        key={comp.type}
                        onClick={() => setSelectedComponentType(comp.type)}
                        className={`card-glow p-4 text-left transition-all hover:border-${comp.color}/50`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-lg bg-${comp.color}/20 text-lg`}
                          >
                            {comp.icon}
                          </div>
                          <div>
                            <h4 className="font-display font-bold text-primary">{comp.label}</h4>
                            <p className="text-xs text-muted">{comp.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Skill Creation Form */}
              {fileAddMode === "create" &&
                selectedComponentType === "SKILL" &&
                createStep === "form" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-primary">Create a Skill</h3>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="text-xs text-muted hover:text-secondary"
                      >
                        ← Back
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">Name *</label>
                      <input
                        type="text"
                        value={skillForm.name}
                        onChange={(e) =>
                          setSkillForm({
                            ...skillForm,
                            name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                          })
                        }
                        placeholder="my-skill-name"
                        className="input-field"
                      />
                      <p className="mt-1 font-mono text-xs text-muted">
                        Lowercase letters, numbers, and hyphens only
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Description *
                      </label>
                      <textarea
                        value={skillForm.description}
                        onChange={(e) =>
                          setSkillForm({ ...skillForm, description: e.target.value })
                        }
                        placeholder="What this skill does and when Claude should use it"
                        rows={2}
                        className="input-field resize-none"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Allowed Tools (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = skillForm.allowedTools.includes(tool)
                                ? skillForm.allowedTools.filter((t) => t !== tool)
                                : [...skillForm.allowedTools, tool];
                              setSkillForm({ ...skillForm, allowedTools: tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              skillForm.allowedTools.includes(tool)
                                ? "bg-purple-500/30 text-purple-300 ring-1 ring-purple-500/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Model (optional)
                      </label>
                      <select
                        value={skillForm.model}
                        onChange={(e) => setSkillForm({ ...skillForm, model: e.target.value })}
                        className="input-field"
                      >
                        <option value="">Default (inherit from conversation)</option>
                        <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                        <option value="claude-opus-4-20250514">Claude Opus 4</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Skill Instructions *
                      </label>
                      <textarea
                        value={skillForm.content}
                        onChange={(e) => setSkillForm({ ...skillForm, content: e.target.value })}
                        rows={8}
                        className="input-field resize-none font-mono text-xs"
                      />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleGeneratePreview}
                        disabled={!skillForm.name || !skillForm.description}
                        className="btn-primary flex-1"
                      >
                        Preview File
                      </button>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              {/* Command Creation Form */}
              {fileAddMode === "create" &&
                selectedComponentType === "COMMAND" &&
                createStep === "form" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-primary">Create a Command</h3>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="text-xs text-muted hover:text-secondary"
                      >
                        ← Back
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Command Name *
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-muted">/</span>
                        <input
                          type="text"
                          value={commandForm.name}
                          onChange={(e) =>
                            setCommandForm({
                              ...commandForm,
                              name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                            })
                          }
                          placeholder="my-command"
                          className="input-field flex-1"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        value={commandForm.description}
                        onChange={(e) =>
                          setCommandForm({ ...commandForm, description: e.target.value })
                        }
                        placeholder="Brief description of what this command does"
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Argument Hint (optional)
                      </label>
                      <input
                        type="text"
                        value={commandForm.argumentHint}
                        onChange={(e) =>
                          setCommandForm({ ...commandForm, argumentHint: e.target.value })
                        }
                        placeholder="[file] [options]"
                        className="input-field"
                      />
                      <p className="mt-1 font-mono text-xs text-muted">
                        Shown in autocomplete, e.g. [message] or [file] [--verbose]
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Allowed Tools (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = commandForm.allowedTools.includes(tool)
                                ? commandForm.allowedTools.filter((t) => t !== tool)
                                : [...commandForm.allowedTools, tool];
                              setCommandForm({ ...commandForm, allowedTools: tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              commandForm.allowedTools.includes(tool)
                                ? "bg-blue-500/30 text-blue-300 ring-1 ring-blue-500/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Command Instructions *
                      </label>
                      <textarea
                        value={commandForm.content}
                        onChange={(e) =>
                          setCommandForm({ ...commandForm, content: e.target.value })
                        }
                        rows={6}
                        className="input-field resize-none font-mono text-xs"
                      />
                      <p className="mt-1 font-mono text-xs text-muted">
                        Use $ARGUMENTS for all args, $1 $2 for individual args
                      </p>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleGeneratePreview}
                        disabled={!commandForm.name}
                        className="btn-primary flex-1"
                      >
                        Preview File
                      </button>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              {/* Agent Creation Form */}
              {fileAddMode === "create" &&
                selectedComponentType === "AGENT" &&
                createStep === "form" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-primary">Create an Agent</h3>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="text-xs text-muted hover:text-secondary"
                      >
                        ← Back
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Agent Name *
                      </label>
                      <input
                        type="text"
                        value={agentForm.name}
                        onChange={(e) =>
                          setAgentForm({
                            ...agentForm,
                            name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                          })
                        }
                        placeholder="my-agent"
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Description *
                      </label>
                      <textarea
                        value={agentForm.description}
                        onChange={(e) =>
                          setAgentForm({ ...agentForm, description: e.target.value })
                        }
                        placeholder="When Claude should invoke this agent and what it specializes in"
                        rows={2}
                        className="input-field resize-none"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Available Tools (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <button
                            key={tool}
                            type="button"
                            onClick={() => {
                              const tools = agentForm.tools.includes(tool)
                                ? agentForm.tools.filter((t) => t !== tool)
                                : [...agentForm.tools, tool];
                              setAgentForm({ ...agentForm, tools });
                            }}
                            className={`rounded-full px-2 py-1 font-mono text-xs transition-colors ${
                              agentForm.tools.includes(tool)
                                ? "bg-amber/30 text-amber ring-1 ring-amber/50"
                                : "bg-elevated text-muted hover:bg-elevated/80"
                            }`}
                          >
                            {tool}
                          </button>
                        ))}
                      </div>
                      <p className="mt-1 font-mono text-xs text-muted">
                        Leave empty to inherit all tools
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Model</label>
                        <select
                          value={agentForm.model}
                          onChange={(e) => setAgentForm({ ...agentForm, model: e.target.value })}
                          className="input-field"
                        >
                          <option value="">Default</option>
                          <option value="sonnet">Sonnet (fast)</option>
                          <option value="opus">Opus (powerful)</option>
                          <option value="haiku">Haiku (quick)</option>
                          <option value="inherit">Inherit from parent</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">
                          Permission Mode
                        </label>
                        <select
                          value={agentForm.permissionMode}
                          onChange={(e) =>
                            setAgentForm({ ...agentForm, permissionMode: e.target.value })
                          }
                          className="input-field"
                        >
                          <option value="default">Default</option>
                          <option value="acceptEdits">Accept Edits</option>
                          <option value="bypassPermissions">Bypass Permissions</option>
                          <option value="plan">Plan Mode</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        System Prompt *
                      </label>
                      <textarea
                        value={agentForm.content}
                        onChange={(e) => setAgentForm({ ...agentForm, content: e.target.value })}
                        rows={8}
                        className="input-field resize-none font-mono text-xs"
                      />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleGeneratePreview}
                        disabled={!agentForm.name || !agentForm.description}
                        className="btn-primary flex-1"
                      >
                        Preview File
                      </button>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              {/* Hook Creation Form */}
              {fileAddMode === "create" &&
                selectedComponentType === "HOOK" &&
                createStep === "form" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-primary">Create a Hook</h3>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="text-xs text-muted hover:text-secondary"
                      >
                        ← Back
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">Event *</label>
                      <select
                        value={hookForm.event}
                        onChange={(e) => setHookForm({ ...hookForm, event: e.target.value })}
                        className="input-field"
                      >
                        {HOOK_EVENTS.map((event) => (
                          <option key={event.value} value={event.value}>
                            {event.label} - {event.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Matcher (optional)
                      </label>
                      <input
                        type="text"
                        value={hookForm.matcher}
                        onChange={(e) => setHookForm({ ...hookForm, matcher: e.target.value })}
                        placeholder="Write|Edit"
                        className="input-field"
                      />
                      <p className="mt-1 font-mono text-xs text-muted">
                        Regex pattern to match tool names, e.g. "Write|Edit" or "Bash"
                      </p>
                    </div>

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">Hook Type *</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setHookForm({ ...hookForm, hookType: "command" })}
                          className={`flex-1 rounded-lg p-3 text-left transition-colors ${
                            hookForm.hookType === "command"
                              ? "bg-success/20 ring-1 ring-success/50"
                              : "bg-elevated hover:bg-elevated/80"
                          }`}
                        >
                          <p className="font-mono text-sm font-medium text-primary">Command</p>
                          <p className="text-xs text-muted">Run a bash script</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setHookForm({ ...hookForm, hookType: "prompt" })}
                          className={`flex-1 rounded-lg p-3 text-left transition-colors ${
                            hookForm.hookType === "prompt"
                              ? "bg-success/20 ring-1 ring-success/50"
                              : "bg-elevated hover:bg-elevated/80"
                          }`}
                        >
                          <p className="font-mono text-sm font-medium text-primary">Prompt</p>
                          <p className="text-xs text-muted">LLM evaluation</p>
                        </button>
                      </div>
                    </div>

                    {hookForm.hookType === "command" ? (
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Command *</label>
                        <input
                          type="text"
                          value={hookForm.command}
                          onChange={(e) => setHookForm({ ...hookForm, command: e.target.value })}
                          placeholder="${CLAUDE_PLUGIN_ROOT}/scripts/my-hook.sh"
                          className="input-field font-mono"
                        />
                        <p className="mt-1 font-mono text-xs text-muted">
                          Use ${"{CLAUDE_PLUGIN_ROOT}"} for plugin directory
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1 block font-mono text-xs text-muted">Prompt *</label>
                        <textarea
                          value={hookForm.prompt}
                          onChange={(e) => setHookForm({ ...hookForm, prompt: e.target.value })}
                          rows={4}
                          className="input-field resize-none font-mono text-xs"
                          placeholder="Evaluate if the task was completed successfully..."
                        />
                      </div>
                    )}

                    <div>
                      <label className="mb-1 block font-mono text-xs text-muted">
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        value={hookForm.timeout}
                        onChange={(e) =>
                          setHookForm({ ...hookForm, timeout: parseInt(e.target.value) || 30 })
                        }
                        min={1}
                        max={300}
                        className="input-field w-32"
                      />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleGeneratePreview}
                        disabled={
                          !hookForm.event ||
                          (hookForm.hookType === "command" ? !hookForm.command : !hookForm.prompt)
                        }
                        className="btn-primary flex-1"
                      >
                        Preview File
                      </button>
                      <button
                        onClick={() => setSelectedComponentType(null)}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              {/* Preview Step */}
              {fileAddMode === "create" && createStep === "preview" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-primary">
                      Preview: {generatedFileName}
                    </h3>
                    <button
                      onClick={() => setCreateStep("form")}
                      className="text-xs text-muted hover:text-secondary"
                    >
                      ← Edit
                    </button>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-elevated bg-base">
                    <div className="border-b border-elevated bg-surface/50 px-3 py-2">
                      <span className="font-mono text-xs text-muted">{generatedFileName}</span>
                    </div>
                    <pre className="max-h-64 overflow-auto p-4 font-mono text-xs text-secondary">
                      {generatedContent}
                    </pre>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={handleSaveComponent}
                      disabled={uploadingFile}
                      className="btn-primary flex-1"
                    >
                      {uploadingFile ? "Saving..." : "Save & Add to Plugin"}
                    </button>
                    <button onClick={() => setCreateStep("form")} className="btn-secondary flex-1">
                      Back to Edit
                    </button>
                  </div>
                </div>
              )}

              {/* File Upload Mode */}
              {fileAddMode === "upload" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-primary">Upload a File</h3>
                    <button
                      onClick={resetCreationWizard}
                      className="text-xs text-muted hover:text-secondary"
                    >
                      ← Back
                    </button>
                  </div>

                  <div>
                    <label className="mb-2 block font-mono text-xs text-muted">File Type</label>
                    <select
                      value={selectedFileType}
                      onChange={(e) =>
                        setSelectedFileType(e.target.value as PluginFile["fileType"])
                      }
                      className="input-field"
                    >
                      {FILE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label} - {opt.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-elevated bg-surface/30 p-8 transition-colors hover:border-amber/50 hover:bg-surface/50">
                    <UploadIcon size={32} className="mb-2 text-muted" />
                    <span className="font-mono text-sm text-muted">
                      {uploadingFile ? "Uploading..." : "Click to upload a file"}
                    </span>
                    <span className="mt-1 font-mono text-xs text-muted">
                      Any file type supported
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        handleFileUpload(e);
                        resetCreationWizard();
                      }}
                      disabled={uploadingFile}
                    />
                  </label>
                </div>
              )}

              {/* Uploaded files list - always visible */}
              {fileAddMode === "select" && (
                <div>
                  <h3 className="mb-3 font-mono text-sm text-muted">
                    Plugin Files ({plugin.files.length})
                  </h3>
                  {plugin.files.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted">
                      No files yet. Create or upload your first component above.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {plugin.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between rounded-lg border border-elevated bg-surface/50 p-3"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm ${
                                file.fileType === "SKILL"
                                  ? "bg-purple-500/20 text-purple-400"
                                  : file.fileType === "COMMAND"
                                    ? "bg-blue-500/20 text-blue-400"
                                    : file.fileType === "AGENT"
                                      ? "bg-amber/20 text-amber"
                                      : file.fileType === "HOOK"
                                        ? "bg-success/20 text-success"
                                        : "bg-elevated text-muted"
                              }`}
                            >
                              {file.fileType === "SKILL" ? (
                                "⚡"
                              ) : file.fileType === "COMMAND" ? (
                                "/"
                              ) : file.fileType === "AGENT" ? (
                                "🤖"
                              ) : file.fileType === "HOOK" ? (
                                "🪝"
                              ) : (
                                <FileIcon size={16} />
                              )}
                            </div>
                            <div>
                              <p className="font-mono text-sm text-secondary">
                                {file.displayName || file.fileName}
                              </p>
                              <p className="font-mono text-xs text-muted">
                                {file.fileType} • {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="rounded p-1 text-muted hover:bg-danger/20 hover:text-danger"
                          >
                            <TrashIcon size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              {fileAddMode === "select" && (
                <div className="flex items-center gap-3 pt-4">
                  <button
                    onClick={() => setStep("review")}
                    disabled={plugin.files.length === 0}
                    className="btn-primary flex-1"
                  >
                    Continue to Review
                  </button>
                  <button onClick={() => setStep("metadata")} className="btn-secondary flex-1">
                    Back
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review */}
          {mode !== "choose" && step === "review" && plugin && (
            <div className="space-y-6">
              <div className="rounded-xl border border-elevated bg-surface/50 p-4">
                <h3 className="mb-4 font-display text-lg font-bold text-primary">Plugin Summary</h3>

                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Name</dt>
                    <dd className="font-mono text-sm text-secondary">{plugin.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Slug</dt>
                    <dd className="font-mono text-sm text-secondary">{plugin.slug}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Tags</dt>
                    <dd className="font-mono text-sm text-secondary">
                      {plugin.tags.length > 0 ? plugin.tags.join(", ") : "None"}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Version</dt>
                    <dd className="font-mono text-sm text-secondary">
                      {plugin.versions[0]?.version || "1.0.0"}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Files</dt>
                    <dd className="font-mono text-sm text-secondary">
                      {plugin.files.length} files
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-mono text-sm text-muted">Status</dt>
                    <dd className="font-mono text-sm text-amber">
                      {getStatusLabel(plugin.status)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-4 border-t border-elevated pt-4">
                  <p className="font-mono text-sm text-muted">Description</p>
                  <p className="mt-1 text-sm text-secondary">{plugin.description}</p>
                </div>
              </div>

              <div className="rounded-lg border border-elevated bg-surface/50 p-4">
                <p className="text-sm text-muted">
                  Submit for review to publish your plugin to the marketplace, or save as draft to
                  continue editing later.
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={handleSubmitForReview}
                  disabled={isLoading}
                  className="btn-primary w-full"
                >
                  {isLoading ? "Submitting..." : "Submit for Review"}
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      onSuccess();
                      onClose();
                    }}
                    className="btn-secondary flex-1"
                  >
                    Save as Draft
                  </button>
                  <button onClick={() => setStep("files")} className="btn-secondary flex-1">
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
